import {chunk, flatMap} from 'lodash';
import {format} from 'date-fns';
import InventoryService, {LocationContent, LpnResponse} from '../../../services/InventoryService';
import ItemMasterService, {Item} from '../../../services/ItemMasterService';
import {LocationContentsGridLocationContent} from './useLocationContentsGrid';

export default class LocationContentsGridHelpers {
  private inventoryService: InventoryService;
  private itemMasterService: ItemMasterService;

  constructor(inventoryService: InventoryService, itemMasterService: ItemMasterService) {
    this.inventoryService = inventoryService;
    this.itemMasterService = itemMasterService;
  }

  /**
   * Fetches all location contents for a given LPN ID and its children,
   * fully mapped and ready for display in the grid.
   * Handles pagination internally and returns all contents in a single array.
   *
   * @param lpnId - The parent LPN ID to fetch contents for
   * @returns Promise resolving to an array of LocationContentsGridLocationContent ready for the grid
   */
  public async fetchAllLpnContents(lpnId: number): Promise<LocationContentsGridLocationContent[]> {
    const childLpns = await this.getLpnChildren(lpnId);
    const lpnIds = [lpnId, ...childLpns.map(({id}) => id)];

    const allContents = await this.fetchAllLocationContentsByLpnIds(lpnIds);

    if (allContents.length === 0) {
      return [];
    }

    const itemIds = Array.from(new Set(allContents.map(({inventoryId}) => inventoryId)));
    const itemMasterItems = await this.getItemMasterItemsByIds(itemIds);

    const inventoriesById = new Map(itemMasterItems.map((item) => [item.id, item]));
    const childLpnsById = new Map(childLpns.map((lpn) => [lpn.id, lpn]));

    return allContents.map((content) => {
      const lpn = childLpnsById.get(content.lpnId);
      return this.mapContentToGrid(content, inventoriesById, lpn);
    });
  }

  /**
   * Fetches all location contents for a given location ID,
   * fully mapped and ready for display in the grid.
   * Handles pagination internally and returns all contents in a single array.
   *
   * @param locationId - The location ID to fetch contents for
   * @returns Promise resolving to an array of LocationContentsGridLocationContent ready for the grid
   */
  public async fetchAllLocationContents(locationId: number): Promise<LocationContentsGridLocationContent[]> {
    const allContents = await this.fetchAllLocationContentsByLocationIds(locationId);

    if (allContents.length === 0) {
      return [];
    }

    const itemIds = Array.from(new Set(allContents.map(({inventoryId}) => inventoryId)));
    const itemMasterItemsPromise = this.getItemMasterItemsByIds(itemIds);

    const contentLpnIds = allContents.map(({lpnId}) => lpnId).filter(Boolean);
    const distinctContentLpnIds = Array.from(new Set(contentLpnIds));

    let allLpns: LpnResponse[] = [];
    if (distinctContentLpnIds.length > 0) {
      const lpns = await this.getAllLpns(distinctContentLpnIds);
      allLpns = lpns;

      const parentLpnIds = lpns.map(({parentId}) => parentId).filter(Boolean);
      const distinctParentLpnIds = Array.from(new Set(parentLpnIds));
      if (distinctParentLpnIds.length > 0) {
        const parentLpns = await this.getAllLpns(distinctParentLpnIds);
        allLpns = [...allLpns, ...parentLpns];
      }
    }

    const itemMasterItems = await itemMasterItemsPromise;

    const inventoriesById = new Map(itemMasterItems.map((item) => [item.id, item]));
    const lpnsById = new Map(allLpns.map((lpn) => [lpn.id, lpn]));

    return allContents.map((content) => {
      const lpn = lpnsById.get(content.lpnId);
      const parentLpn = lpn ? lpnsById.get(lpn.parentId) : undefined;

      return this.mapContentToGrid(content, inventoriesById, lpn, parentLpn);
    });
  }

  /**
   * Fetches all location contents by LPN IDs, handling pagination internally.
   * Batches LPN ID requests to avoid URL size limits (100 per batch).
   */
  private async fetchAllLocationContentsByLpnIds(lpnIds: number[]): Promise<LocationContent[]> {
    const batchSize = 100;
    const lpnIdBatches = chunk(lpnIds, batchSize);

    const allContents: LocationContent[] = [];
    for (const batchLpnIds of lpnIdBatches) {
      const batchContents = await this.fetchAllPages(
        (continuationToken) => this.inventoryService.getLocationContentsByLpnIds(batchLpnIds, continuationToken),
        ({data}) => ({
          continuationToken: data.continuationToken,
          pageElements: data.contents
        })
      );
      allContents.push(...batchContents);
    }

    return allContents;
  }

  /**
   * Fetches all location contents by location ID, handling pagination internally.
   */
  private async fetchAllLocationContentsByLocationIds(locationId: number): Promise<LocationContent[]> {
    return this.fetchAllPages(
      (continuationToken) =>
        this.inventoryService.getLocationContentsByLocationIds([locationId], continuationToken, true),
      ({data}) => ({
        continuationToken: data.continuationToken,
        pageElements: data.contents
      })
    );
  }

  /**
   * Fetches all item master items for the given item IDs.
   * Batches requests to avoid URL size limits (100 per batch).
   */
  private async getItemMasterItemsByIds(itemIds: number[]): Promise<Item[]> {
    const batchSize = 100;
    const itemIdBatches = chunk(itemIds, batchSize);

    const inventoryItemsPromises = itemIdBatches.map(async (batchItemIds) => {
      // Continuation token of null is ok because batch size is smaller than page size.
      // There will only ever be one page.
      const continuationToken = null;
      return (await this.itemMasterService.getItems(batchItemIds, continuationToken)).data.items;
    });

    const inventoryItemBatchResults = await Promise.all(inventoryItemsPromises);
    return flatMap(inventoryItemBatchResults);
  }

  /**
   * Fetches all child LPNs for a given parent LPN ID, handling pagination internally.
   */
  private async getLpnChildren(lpnId: number): Promise<LpnResponse[]> {
    return this.fetchAllPages(
      (continuationToken) => this.inventoryService.getLpnChildren(lpnId, continuationToken),
      ({data}) => ({
        continuationToken: data.continuationToken,
        pageElements: data.lpnDetails
      })
    );
  }

  /**
   * Fetches all LPNs for the given LPN IDs.
   * Batches requests to avoid URL size limits (100 per batch).
   */
  private async getAllLpns(lpnIds: number[]): Promise<LpnResponse[]> {
    const batchSize = 100;
    const lpnIdBatches = chunk(lpnIds, batchSize);
    const lpnsPromises = lpnIdBatches.map(
      async (batchLpnIds) => (await this.inventoryService.getLpns(batchLpnIds)).data
    );

    const lpnBatchesResults = await Promise.all(lpnsPromises);
    return flatMap(lpnBatchesResults);
  }

  /**
   * Generic function to fetch all pages of paginated data.
   * Continues fetching until no continuation token is returned.
   *
   * @param getPage - Function that fetches a single page given a continuation token
   * @param mapResponse - Function that extracts continuation token and page elements from response
   * @returns Promise resolving to all results from all pages combined
   */
  private async fetchAllPages<TResult, TResponse>(
    getPage: (continuationToken: string | null) => Promise<TResponse>,
    mapResponse: (response: TResponse) => {continuationToken: string | null; pageElements: TResult[]}
  ): Promise<TResult[]> {
    let currentContinuationToken: string | null = null;
    let results: TResult[] = [];
    do {
      const response = await getPage(currentContinuationToken);
      const {continuationToken, pageElements} = mapResponse(response);
      currentContinuationToken = continuationToken;
      results = [...results, ...pageElements];
    } while (currentContinuationToken);
    return results;
  }

  /**
   * Format a date string to M/D/YY format
   */
  private formatDate(dateAsString: string): string | null {
    if (dateAsString) {
      return format(dateAsString, 'M/D/YY');
    }
    return null;
  }

  /**
   * Format a datetime string to M/D/YY hh:mm A format
   */
  private formatDateTime(dateTimeAsString: string): string | null {
    if (dateTimeAsString) {
      return format(dateTimeAsString, 'M/D/YY hh:mm A');
    }
    return null;
  }

  /**
   * Maps location content to grid format for display.
   * Handles both LPN-specific and location-specific views.
   */
  private mapContentToGrid(
    content: LocationContent,
    inventoriesById: Map<number, Item>,
    lpn?: LpnResponse,
    parentLpn?: LpnResponse
  ): LocationContentsGridLocationContent {
    const inventoryItem = inventoriesById.get(content.inventoryId);
    const upcBarcode = inventoryItem?.properties.find(({packaging}) => packaging === content.quantity.packaging)
      ?.barcode;

    return {
      reservationId: content.reservationId,
      parentLpnBarcode: parentLpn?.barcode,
      parentLpnId: parentLpn?.id,
      lpnBarcode: lpn?.barcode,
      lpnId: lpn?.id,
      id: content.id.toString(),
      sku: {id: content.inventoryId, sku: inventoryItem?.sku || ''},
      description: inventoryItem?.description || '',
      upc: upcBarcode,
      quantity: content.quantity.amount,
      unit: {
        amount: content.quantity.amount,
        unit: content.quantity.packaging
      },
      uomJson: content.inventoryTrackingData?.uoms,
      lotCode: content.inventoryTrackingData?.lotCode,
      expirationDate: this.formatDate(content.inventoryTrackingData?.expirationDate),
      manufactureDate: this.formatDateTime(content.inventoryTrackingData?.manufactureDate)
    };
  }
}
